import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { IMAGES, ORG } from "@/lib/constants";
import {
  Heart,
  PartyPopper,
  Sparkles,
  Briefcase,
  Music2,
  Flower2,
  Camera,
  Users,
  Check,
  Minus,
  ArrowUpRight,
  Phone,
  Mail,
  Compass,
  Clock,
  MailCheck,
  ShieldCheck,
  ArrowDownRight,
  CalendarCheck,
  FileText,
  Wine,
  Shield,
  Building2,
  Wrench,
  Flame,
  XCircle,
} from "lucide-react";

const EVENTS_WE_HOST = [
  { icon: Heart, t: "Weddings" },
  { icon: PartyPopper, t: "Wedding Receptions" },
  { icon: Sparkles, t: "Galas" },
  { icon: Briefcase, t: "Corporate Events" },
  { icon: Music2, t: "Cultural Events" },
  { icon: Flower2, t: "Memorials & Celebrations of Life" },
  { icon: Camera, t: "Photo & Film Shoots" },
  { icon: Users, t: "Community Events" },
];

const CAPACITY_TILES = [
  { t: "Seated Events", b: "Round tables seat guests. Rectangular tables are typically used for food, service, and gift stations. Tables and chairs are included with the rental. Final capacity is confirmed during the walkthrough." },
  { t: "Standing Receptions", b: "Cocktail-style and open-floor receptions. Layout flexes around your program flow. Final capacity is confirmed during the walkthrough." },
  { t: "Ceremony or Program Seating", b: "Aisle, row, or theater-style program seating. Suited to ceremonies, presentations, and panel-style programs. Final capacity is confirmed during the walkthrough." },
];

const INCLUDED = [
  "Historic venue access",
  "Event space use",
  "Tables and chairs included with rental",
  "Restroom access",
  "Walkthrough coordination",
  "Mission-centered venue experience",
];

const NOT_INCLUDED = [
  "Catering",
  "Linens",
  "Decorations",
  "Entertainment",
  "Security, if required",
  "Insurance, if required",
  "Permits, if required",
  "Setup and cleanup beyond agreement terms",
];

const POLICIES = [
  { icon: FileText, t: "Catering & Vendors", b: "Preferred vendor list and outside-vendor approvals are handled through the rental agreement." },
  { icon: Wine, t: "Alcohol", b: "If served, licensed bartending and any required permits may apply. Details confirmed in the agreement." },
  { icon: Shield, t: "Insurance", b: "Renters may be required to provide a Certificate of Insurance for the event date." },
  { icon: ArrowDownRight, t: "Damage Deposit", b: "A refundable damage deposit may be required and is outlined in the agreement." },
  { icon: Users, t: "Security", b: "On-site security may be required depending on event size, type, and time." },
  { icon: Wrench, t: "Setup & Cleanup", b: "Setup, teardown, and cleanup windows are defined in the rental agreement." },
  { icon: Building2, t: "Historic Preservation", b: "Historic Engine 11 is a preserved Detroit landmark; protective guidelines apply." },
  { icon: Flame, t: "Open Flames & Special Effects", b: "Open flames, candles, sparklers, confetti, and special effects require advance approval and may be limited or prohibited. No sparklers. No confetti." },
  { icon: XCircle, t: "Cancellation", b: "Cancellation terms and deadlines are outlined in the rental agreement." },
];

const FAQS = [
  {
    q: "Can we schedule a walkthrough before booking?",
    a: "Yes. Walkthroughs are encouraged and are coordinated through the rental inquiry process. Use the form below or request a walkthrough directly via Contact.",
  },
  {
    q: "How far in advance should we inquire?",
    a: "We recommend inquiring as early as possible — particularly for weekend dates and peak seasons. Specific lead times are confirmed during the inquiry process.",
  },
  {
    q: "Can we use our own caterer?",
    a: "All food served at Historic Engine 11 must be catered. Phoenix can suggest preferred caterers on request. Outside caterers do not require special approval simply for being outside caterers — final catering terms are confirmed in the rental agreement.",
  },
  {
    q: "Is alcohol allowed?",
    a: "Alcohol service is reviewed on a per-event basis. Licensed bartending, permits, and applicable terms are confirmed in the rental agreement.",
  },
  {
    q: "Are open flames, candles, sparklers, or special effects allowed?",
    a: "Open flames, candles, sparklers, confetti, and special effects require advance approval and may be limited or prohibited under historic preservation guidelines. No sparklers. No confetti.",
  },
  {
    q: "Is parking available?",
    a: "Parking guidance is provided during the inquiry process and confirmed in the rental agreement.",
  },
  {
    q: "Is the venue accessible?",
    a: "Yes. Historic Engine 11 is handicap accessible. Specific accessibility considerations are reviewed during the walkthrough — please share any particular needs in your inquiry.",
  },
  {
    q: "Can we rent the venue for multiple days?",
    a: "Multi-day rentals are considered on a case-by-case basis, subject to availability and the terms of the rental lease agreement. Details are confirmed during the inquiry process.",
  },
];

const fieldCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 transition-colors";
const selectCls = `${fieldCls} appearance-none pr-10`;

function Select({ name, value, onChange, options, placeholder, testid }) {
  return (
    <div className="relative">
      <select name={name} value={value} onChange={onChange} className={selectCls} data-testid={testid}>
        <option value="" className="bg-[#1A1A1A]">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#1A1A1A]">{o}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-amber-500">▾</span>
    </div>
  );
}

export default function RentEngine11() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_date: "", event_type: "", guest_count: "", notes: "",
    organization: "",
    rental_type: "",
    estimated_duration: "",
    flexibility: "",
    referral_source: "",
    walkthrough_requested: false,
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
      };
      await api.post("/rental-inquiry", payload);
      toast.success("Inquiry sent. We'll reach out shortly.");
      setForm({
        name: "", email: "", phone: "",
        event_date: "", event_type: "", guest_count: "", notes: "",
        organization: "", rental_type: "", estimated_duration: "",
        flexibility: "", referral_source: "", walkthrough_requested: false,
      });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = (e) => {
    e?.preventDefault();
    document.getElementById("rental-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const mapsHref = `https://www.google.com/maps?q=${encodeURIComponent(ORG.mapsQuery)}`;

  return (
    <div data-testid="rent-engine11-page">
      <PageMeta
        title="Rent Historic Engine 11"
        description="Host weddings, receptions, galas, corporate gatherings, and cultural events inside a Detroit landmark. Every rental supports Phoenix preservation and community programs."
        path="/rent-engine-11"
      />
      <PageHero
        overline="Rent Historic Engine 11"
        title="Host your moment inside a Detroit landmark."
        subtitle="Weddings. Receptions. Galas. Corporate gatherings. Cultural events. Engine 11's character cannot be replicated — every rental supports preservation and Phoenix programs."
        image={IMAGES.engine11}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <a href="#rental-form-anchor" onClick={scrollToForm} className="btn-gold w-full sm:w-auto justify-center" data-testid="hero-rental-cta">
            Submit a Rental Inquiry <ArrowDownRight className="h-4 w-4" />
          </a>
          <Link
            to="/contact?type=rent-engine-11&subject=Walkthrough%20Request"
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="hero-walkthrough-cta"
          >
            Schedule a Walkthrough <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </PageHero>

      {/* ----- DIRECT CONTACT ----- */}
      <section className="px-5 lg:px-8 -mt-6 lg:-mt-10" data-testid="rent-direct">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a href={`tel:${ORG.phone.replace(/[^0-9+]/g, "")}`} className="card-dark p-5 flex items-center gap-4 hover:border-amber-500/40">
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Phone className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <div className="overline">Phone</div>
              <div className="text-white text-sm mt-1 truncate">{ORG.phone}</div>
            </span>
          </a>
          <a
            href={ORG.emailConfirmed ? `mailto:${ORG.email}` : undefined}
            className={`card-dark p-5 flex items-center gap-4 ${ORG.emailConfirmed ? "hover:border-amber-500/40" : "opacity-80"}`}
            aria-disabled={!ORG.emailConfirmed}
          >
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Mail className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <div className="overline">Email</div>
              <div className="text-white text-sm mt-1 break-all">{ORG.emailConfirmed ? ORG.email : "Email coming soon"}</div>
            </span>
          </a>
          <a href={mapsHref} target="_blank" rel="noreferrer" className="card-dark p-5 flex items-center gap-4 hover:border-amber-500/40">
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Compass className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <div className="overline">Map / Directions</div>
              <div className="text-white text-sm mt-1 break-words">{ORG.address}</div>
            </span>
          </a>
        </div>
        <div className="max-w-7xl mx-auto mt-3 text-xs text-slate-400 flex items-center gap-2 px-1">
          <Clock className="h-3.5 w-3.5 text-amber-500" />
          We typically respond within 5–7 business days.
        </div>
      </section>

      {/* ----- EVENTS WE HOST ----- */}
      <section className="py-12 lg:py-24" data-testid="events-we-host">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading overline="Events We Host" title="Bring your moment to Engine 11." subtitle="Historic Engine 11 hosts a wide range of events. Specific accommodations are reviewed during the inquiry process." />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {EVENTS_WE_HOST.map((e) => (
              <div key={e.t} className="card-dark p-5 text-center">
                <e.icon className="h-6 w-6 text-amber-500 mx-auto" />
                <div className="mt-3 font-display text-base lg:text-lg text-white leading-tight">{e.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- CAPACITY & LAYOUT ----- */}
      <section className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="capacity-layout">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Capacity & Layout"
            title="Layout options that flex with your event."
            subtitle="Capacity and layout options vary by event type, room configuration, and final setup needs. Final capacity will be confirmed during the inquiry and walkthrough process."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CAPACITY_TILES.map((c) => (
              <div key={c.t} className="card-dark p-7">
                <div className="overline">Configuration</div>
                <div className="font-display text-2xl text-white mt-2">{c.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed">{c.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- PRICING LANGUAGE BAND ----- */}
      <section className="py-16 lg:py-20" data-testid="pricing-band">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-12">
            <img src={IMAGES.engine11} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-red-950/60" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="overline">Rental Pricing</div>
                <h3 className="font-display text-3xl lg:text-4xl text-white mt-3 leading-[1.05]">
                  Custom quotes. Mission-driven rentals.
                </h3>
                <p className="text-slate-300 mt-4 leading-relaxed max-w-2xl">
                  Rental rates differ primarily for active Detroit firefighters
                  versus civilian rentals. Event duration, setup needs, and any
                  multi-day rental terms (governed by the rental lease
                  agreement) may also affect the final quote. We'll work with
                  you to build a custom quote after you submit an inquiry.
                  Every rental directly supports the Phoenix mission and the
                  preservation of Historic Engine 11.
                </p>
              </div>
              <div className="lg:col-span-4 flex">
                <a href="#rental-form-anchor" onClick={scrollToForm} className="btn-gold w-full justify-center" data-testid="pricing-quote-btn">
                  Submit a Rental Inquiry <ArrowDownRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- WHAT'S INCLUDED / NOT INCLUDED ----- */}
      <section className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="included-not">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="At-a-Glance"
            title="What's typically included — and what's not."
            subtitle="Final inclusions will be confirmed in the rental agreement."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-dark p-8">
              <div className="overline">Typically Included</div>
              <ul className="mt-5 space-y-3 text-slate-200">
                {INCLUDED.map((i) => (
                  <li key={i} className="flex gap-3"><Check className="h-4 w-4 mt-1 text-amber-500 shrink-0" />{i}</li>
                ))}
              </ul>
            </div>
            <div className="card-dark p-8">
              <div className="overline">Not Included</div>
              <ul className="mt-5 space-y-3 text-slate-200">
                {NOT_INCLUDED.map((i) => (
                  <li key={i} className="flex gap-3"><Minus className="h-4 w-4 mt-1 text-slate-500 shrink-0" />{i}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ----- POLICIES ----- */}
      <section className="py-12 lg:py-24" data-testid="policies">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Rental Policies Overview"
            title="Clear, flexible, and protective."
            subtitle="All policies are subject to final rental agreement. The information below is a general overview."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {POLICIES.map((p) => (
              <div key={p.t} className="card-dark p-6" data-testid={`policy-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p.icon className="h-6 w-6 text-amber-500" />
                <div className="font-display text-lg text-white mt-4">{p.t}</div>
                <div className="text-slate-400 mt-2 text-sm leading-relaxed">{p.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- PHOTO GALLERY — single tasteful placeholder until real event photos arrive ----- */}
      {/* NOTE (Feb 2026): Flagged for future Tier 2 photo replacement — */}
      {/* needs real event photography (weddings, receptions, galas, */}
      {/* community events, venue setup). Do not swap until confirmed. */}
      <section className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="gallery">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-1 ring-amber-500/20 aspect-[16/7]">
            <img src={IMAGES.engine11} alt="Historic Engine 11 — full event gallery coming soon" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />
            <div className="relative h-full w-full flex flex-col items-center justify-center text-center px-6">
              <div className="inline-flex items-center gap-2 text-amber-400 text-xs tracking-[0.32em] uppercase font-accent">
                <Camera className="h-3.5 w-3.5" /> Gallery
              </div>
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white mt-4 leading-tight max-w-xl">
                Event photos coming soon.
              </h3>
              <p className="text-slate-300 mt-4 text-sm sm:text-base leading-relaxed max-w-xl">
                Photography of weddings, receptions, galas, community events,
                and venue setups will be added as planning continues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- FAQ ----- */}
      <section className="py-12 lg:py-24" data-testid="rent-faq">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="FAQ"
            title="Common rental questions."
            subtitle="General answers are provided below. Final terms are confirmed through the rental agreement. If you have specific accessibility, mobility, or sensory considerations, please share them in your inquiry so we can plan for them during the walkthrough."
          />
          <div className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left text-white font-display text-base lg:text-lg hover:text-amber-400" data-testid={`faq-q-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 leading-relaxed text-sm lg:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* (2027 bridge removed — evergreen Rent Engine 11 page) */}

      {/* ----- RENTAL INQUIRY FORM ----- */}
      <section id="rental-form-anchor" className="py-12 lg:py-28 scroll-mt-24" data-testid="rental-form-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading overline="The Venue" title="Historic character. Detroit heart." />
            <ul className="space-y-3 text-slate-300 leading-relaxed">
              {[
                "Historic Detroit firehouse architecture",
                "Flexible event space",
                "Iconic photo opportunities",
                "Community-rooted, mission-driven rental",
                "Proceeds support the Phoenix mission",
              ].map((p) => (
                <li key={p} className="flex gap-3"><span className="text-amber-500">◆</span>{p}</li>
              ))}
            </ul>
            <div className="mt-8 rounded-2xl overflow-hidden ring-ember">
              <img src={IMAGES.engine11} alt="Historic Engine 11" className="w-full object-cover" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="card-dark p-7 lg:p-10">
              <div className="overline">Rental Inquiry</div>
              <h3 className="font-display text-3xl text-white mt-2">Tell us about your event</h3>
              <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rental-form">
                {/* Identity */}
                <input name="name" value={form.name} onChange={onChange} required placeholder="Full Name" className={fieldCls} data-testid="rental-name" />
                <input name="email" type="email" value={form.email} onChange={onChange} required placeholder="Email" className={fieldCls} data-testid="rental-email" />
                <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone" className={fieldCls} data-testid="rental-phone" />
                <input name="organization" value={form.organization} onChange={onChange} placeholder="Organization / Business Name (optional)" className={fieldCls} data-testid="rental-organization" />

                {/* Event */}
                <div className="md:col-span-2">
                  <label className="block overline mb-2">Event Type</label>
                  <Select
                    name="event_type"
                    value={form.event_type}
                    onChange={onChange}
                    placeholder="Select event type…"
                    testid="rental-type"
                    options={["Wedding", "Wedding Reception", "Gala", "Corporate Event", "Cultural Event", "Memorial or Celebration of Life", "Photo or Film Shoot", "Community Event", "Other"]}
                  />
                </div>

                <input name="event_date" type="date" value={form.event_date} onChange={onChange} className={fieldCls} data-testid="rental-date" />
                <input name="guest_count" type="number" min="1" value={form.guest_count} onChange={onChange} placeholder="Approximate Guest Count" className={fieldCls} data-testid="rental-guests" />

                <div>
                  <label className="block overline mb-2">Rental Type</label>
                  <Select
                    name="rental_type"
                    value={form.rental_type}
                    onChange={onChange}
                    placeholder="Select rental type…"
                    testid="rental-rental-type"
                    options={["Ceremony only", "Reception only", "Both", "Full venue", "Photo only", "Other"]}
                  />
                </div>

                <div>
                  <label className="block overline mb-2">Estimated Duration</label>
                  <Select
                    name="estimated_duration"
                    value={form.estimated_duration}
                    onChange={onChange}
                    placeholder="Select duration…"
                    testid="rental-duration"
                    options={["Half-day", "Full-day", "Evening", "Multi-day", "Other"]}
                  />
                </div>

                <div>
                  <label className="block overline mb-2">Date Flexibility</label>
                  <Select
                    name="flexibility"
                    value={form.flexibility}
                    onChange={onChange}
                    placeholder="Select flexibility…"
                    testid="rental-flexibility"
                    options={["Date is fixed", "Flexible within a month", "Flexible within a season", "Still deciding"]}
                  />
                </div>

                <div>
                  <label className="block overline mb-2">Referral Source</label>
                  <Select
                    name="referral_source"
                    value={form.referral_source}
                    onChange={onChange}
                    placeholder="How did you hear about us?"
                    testid="rental-referral"
                    options={["Website", "Word of mouth", "Past event", "Vendor", "Phoenix member", "Other"]}
                  />
                </div>

                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={onChange}
                  rows={5}
                  placeholder="Tell us more about your vision, accessibility needs, or special considerations..."
                  className={`${fieldCls} md:col-span-2`}
                  data-testid="rental-notes"
                />

                <label className="md:col-span-2 flex items-center gap-3 text-slate-200 text-sm">
                  <input
                    type="checkbox"
                    name="walkthrough_requested"
                    checked={form.walkthrough_requested}
                    onChange={onChange}
                    className="h-4 w-4 accent-amber-500"
                    data-testid="rental-walkthrough"
                  />
                  <CalendarCheck className="h-4 w-4 text-amber-500" />
                  I would like to schedule a walkthrough of Historic Engine 11.
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold md:col-span-2 justify-center disabled:opacity-60"
                  data-testid="rental-submit"
                >
                  {loading ? "Sending..." : "Submit Inquiry"}
                </button>

                {/* Footer copy */}
                <div className="md:col-span-2 space-y-3 mt-2 text-sm text-slate-400">
                  <div className="flex items-start gap-3">
                    <MailCheck className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                    <span>You will receive an email confirmation once your inquiry is received.</span>
                  </div>
                  <div className="flex items-start gap-3" data-testid="rental-privacy-copy">
                    <ShieldCheck className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                    <span>
                      By submitting this form, you acknowledge that Phoenix may
                      use the information provided to respond to your inquiry
                      in accordance with our{" "}
                      <Link
                        to="/privacy"
                        className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
                        data-testid="rental-privacy-link"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
